import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "./assets/logo.png";

const fmt = (v) => (v == null ? "" : String(v));
const pad = (n) => {
  const x = String(n || "");
  return x.length === 1 ? `0${x}` : x;
};

/**
 * دالة مساعدة لجلب القيمة سواء كانت snake_case أو camelCase
 */
function getV(obj, key) {
  if (!obj) return "";
  const camel = key.replace(/_([a-z])/g, g => g[1].toUpperCase());
  return obj[key] !== undefined ? obj[key] : (obj[camel] !== undefined ? obj[camel] : "");
}

function fmtNum(v, decimals = 0) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fmt(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export default function generateAirCargoManifestPdf({ header, bols, totals, user, title = "AIR CARGO MANIFEST", fileNamePrefix = "AirCargoManifest" }) {
  const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;

  // 1. Logo (نفس مقاسات وتوسيط الفاتورة)
  const logoWidth = 180;
  const logoHeight = 45;
  const logoX = (pageW - logoWidth) / 2;
  const logoY = 20;
  doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

  // 2. Titles
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(title, margin, logoY + logoHeight + 30);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("I.C.A.O. Annex 9, Appendix 3", pageW - margin, logoY + logoHeight + 30, { align: "right" });
  
  doc.setLineWidth(1.5);
  doc.line(margin, logoY + logoHeight + 35, pageW - margin, logoY + logoHeight + 35);

  // 3. Info Grid (توزيع الحقول بنفس أسلوب الفاتورة)
  const infoStartY = logoY + logoHeight + 55;
  let infoCurrentY = infoStartY;
  const col1X = margin;
  const col2X = pageW / 2 + 50;
  const lineHeight = 14;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  
  // Row 1
  doc.text("Date prepared:", col1X, infoCurrentY);
  doc.setFont("helvetica", "normal");
  doc.text(formatDate(new Date()), col1X + 80, infoCurrentY);
  doc.setFont("helvetica", "bold");
  doc.text("Page:", col2X, infoCurrentY);
  doc.setFont("helvetica", "normal");
  doc.text("01", col2X + 60, infoCurrentY);
  infoCurrentY += lineHeight;

  // Row 2
  doc.setFont("helvetica", "bold");
  doc.text("Owner or Operator:", col1X, infoCurrentY);
  doc.setFont("helvetica", "normal");
  doc.text(fmt(getV(header, "owner_operator") || "TARCO AVIATION"), col1X + 100, infoCurrentY);
  doc.setFont("helvetica", "bold");
  doc.text("Prepared by:", col2X, infoCurrentY);
  doc.setFont("helvetica", "normal");
  doc.text(fmt(user?.username || "Admin"), col2X + 80, infoCurrentY);
  infoCurrentY += lineHeight;

  // Row 3
  doc.setFont("helvetica", "bold");
  doc.text("Registration:", col1X, infoCurrentY);
  doc.setFont("helvetica", "normal");
  doc.text(fmt(getV(header, "registration") || "___________"), col1X + 80, infoCurrentY);
  doc.setFont("helvetica", "bold");
  doc.text("Manifest No:", col2X, infoCurrentY);
  doc.setFont("helvetica", "normal");
  doc.text(fmt(getV(header, "id") || "___________"), col2X + 80, infoCurrentY);
  infoCurrentY += lineHeight;

  // Row 4
  doc.setFont("helvetica", "bold");
  doc.text("Point of Loading:", col1X, infoCurrentY);
  doc.setFont("helvetica", "normal");
  doc.text(fmt(getV(header, "place_of_departure_code") || "___________"), col1X + 100, infoCurrentY);
  doc.setFont("helvetica", "bold");
  doc.text("Customs Ref:", col2X, infoCurrentY);
  doc.setFont("helvetica", "normal");
  doc.text(fmt(getV(header, "customs_office_code") || "___________"), col2X + 80, infoCurrentY);
  infoCurrentY += lineHeight;

  // Row 5: Flight No & Unloading
  doc.setFont("helvetica", "bold");
  doc.text("Flight No:", col1X, infoCurrentY);
  doc.setFont("helvetica", "normal");
  doc.text(fmt(getV(header, "voyage_number") || getV(header, "flight_no") || "___________"), col1X + 80, infoCurrentY);
  doc.setFont("helvetica", "bold");
  doc.text("Point of Unloading:", col2X, infoCurrentY);
  doc.setFont("helvetica", "normal");
  doc.text(fmt(getV(header, "place_of_destination_code") || "___________"), col2X + 100, infoCurrentY);
  infoCurrentY += lineHeight;

  let tableStartY = infoCurrentY + 20;

  // 4. Grouping & Table Logic (بدون مبالغ)
  let totalPieces = 0;
  let totalKg = 0;
  const masterGroups = [];
  if (Array.isArray(bols)) {
    // تحديد الماستر: أي بوليصة لا تملك parent_bol_id
    const masters = bols.filter(b => !getV(b, "parent_bol_id"));
    const houseMap = new Map();
    // تجميع الهاوسات بناءً على الرقم التعريفي للأب
    bols.filter(b => !!getV(b, "parent_bol_id")).forEach((h) => {
      const pid = getV(h, "parent_bol_id");
      if (!houseMap.has(pid)) houseMap.set(pid, []);
      houseMap.get(pid).push(h);
    });
    masters.forEach(master => {
      const masterCopy = { ...master };
      delete masterCopy.homes; 
      const mid = getV(master, "id");
      masterGroups.push({ master: masterCopy, houses: houseMap.get(mid) || [] });
 });
  }

  const bodyWithMeta = [];
  // صف تجميلي BULK
  bodyWithMeta.push([{ content: "BULK CARGO", colSpan: 6, styles: { fillColor: [245, 245, 245], fontStyle: "bold" } }]);

  masterGroups.forEach(group => {
    const { master, houses } = group;
    
    // ✅ منطق التجميع الصحيح:
    // إذا كانت البوليصة تحتوي على هاوسات، نجمع أوزان الهاوسات فقط ونستثني الماستر من الجمع لتجنب التكرار.
    // إذا كانت بوليصة مباشرة (بدون هاوس)، نجمع الماستر.
    if (houses && houses.length > 0) {
      houses.forEach(h => {
        totalPieces += Number(getV(h, "number_of_packages") || 0);
        totalKg += Number(getV(h, "gross_mass") || 0);
      });
    } else {
      totalPieces += Number(getV(master, "number_of_packages") || 0);
      totalKg += Number(getV(master, "gross_mass") || 0);
    }

    // إضافة الماستر (بخط عريض للتمييز)
    bodyWithMeta.push([
      { content: fmt(getV(master, "bol_reference")), styles: { fontStyle: 'bold' } },
      fmtNum(getV(master, "number_of_packages")),
      `${fmtNum(getV(master, "gross_mass"))} KG`,
      fmt(getV(master, "goods_description")),
      `${fmt(getV(header, "place_of_departure_code"))}-${fmt(getV(header, "place_of_destination_code"))}`,
      fmt(master.official_use || "J EAP SPX.")
    ]);

    // إضافة الهاوسات (بإزاحة وسهم تمييز ولون أخف قليلاً)
    houses.forEach(house => {
      bodyWithMeta.push([
        { 
          content: `   ↳ ${fmt(getV(house, "bol_reference"))}`, 
          styles: { fontStyle: 'italic', textColor: [80, 80, 80] } 
        },
        fmtNum(getV(house, "number_of_packages")),
        `${fmtNum(getV(house, "gross_mass"))} KG`,
        fmt(getV(house, "goods_description")),
        `${fmt(getV(header, "place_of_departure_code"))}-${fmt(getV(header, "place_of_destination_code"))}`,
        fmt(house.official_use || "J EAP SPX.")
      ]);
    });
  });

  const tableHeaders = ["Air waybill", "pieces", "weight", "nature of goods", "operator use", "official use"];

  autoTable(doc, {
    startY: tableStartY,
    head: [tableHeaders],
    body: bodyWithMeta,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 5, textColor: [0, 0, 0], font: "helvetica" },
    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold", lineWidth: 0.5, lineColor: [0, 0, 0] },
    bodyStyles: { lineWidth: 0.5, lineColor: [0, 0, 0] },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 50, halign: 'center' },
      2: { cellWidth: 80, halign: 'right' },
      3: { cellWidth: 150 },
      4: { cellWidth: 80 },
      5: { cellWidth: 60 }
    },
  });

  // 5. Final Totals (حساب المجموع من الماستر فقط لتجنب التكرار)
  const finalY = doc.lastAutoTable.finalY + 30;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`TOTAL:          ${fmtNum(totalPieces)} PCS          ${fmtNum(totalKg, 2)} KG`, margin, finalY);

  const safeTitle = fileNamePrefix.replace(/[^a-zA-Z0-9_-]/g, "");
  const fileName = `${safeTitle}_${fmt(getV(header, "voyage_number") || getV(header, "id"))}_${formatDate(getV(header, "date_of_arrival"))}.pdf`;
  doc.save(fileName);
}
